import { useState, useRef, useEffect, useCallback } from 'react';
import { TICKET_SOURCE, DEFAULT_FROM_EMAIL, DEFAULT_FROM_NAME } from '../constants';
import { escapeHtml } from '../utils';
import { sanitizeHtml } from '../email-body';

// ── Gmail-compatible quote format ─────────────────────────────────────
// Replicates Gmail's exact HTML structure so quoted text collapses
// behind "..." in the recipient's Gmail client.

const formatGmailDate = dateObj => {
  const d = dateObj || new Date();
  const weekday = d.toLocaleDateString('en-US', { weekday: 'short' });
  const month = d.toLocaleDateString('en-US', { month: 'short' });
  const day = d.getDate();
  const year = d.getFullYear();
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${weekday}, ${month} ${day}, ${year} at ${time}`;
};

// Gmail format: "On Fri, Jun 5, 2026 at 4:12 PM Name <email> wrote:"
const buildAttribution = (dateObj, name, email) => {
  const date = formatGmailDate(dateObj);
  const senderStr = email
    ? `${escapeHtml(name)} &lt;<a href="mailto:${escapeHtml(email)}">${escapeHtml(
        email,
      )}</a>&gt;`
    : escapeHtml(name);
  return `On ${date} ${senderStr} wrote:`;
};

// Gmail's exact structure: gmail_quote_container triggers collapse
const wrapInGmailQuote = (attribution, contentHtml) =>
  '<div class="gmail_quote gmail_quote_container">' +
  '<div dir="ltr" class="gmail_attr">' +
  attribution +
  '<br></div>' +
  '<blockquote class="gmail_quote" style="margin:0px 0px 0px 0.8ex;' +
  'border-left:1px solid rgb(204,204,204);padding-left:1ex">' +
  contentHtml +
  '</blockquote></div>';

// Build full email HTML: reply text + quoted chain
const buildReplyHtml = (replyText, quotedChain) =>
  '<div dir="ltr">' + replyText + '</div>' + (quotedChain ? '<br>' + quotedChain : '');

const syncQuotedArea = (ref, html) => {
  setTimeout(() => {
    if (ref.current) {
      ref.current.innerHTML = sanitizeHtml(html);
    }
  }, 0);
};

// ── Hook ──────────────────────────────────────────────────────────────

const useEmailChain = (activeTicket, items, internal) => {
  const isEmailTicket = activeTicket?.source === TICKET_SOURCE.EMAIL;
  const [quotedTextIncluded, setQuotedTextIncluded] = useState(false);
  const quotedAreaRef = useRef(null);
  const chainRef = useRef('');
  const chainInitDoneRef = useRef(false);

  // Reset when switching tickets — clear everything including DOM
  useEffect(() => {
    chainRef.current = '';
    chainInitDoneRef.current = false;
    setQuotedTextIncluded(false);
    if (quotedAreaRef.current) quotedAreaRef.current.innerHTML = '';
  }, [activeTicket?.id]);

  // Initialize chain from messages when items load.
  // Only depends on [items] — NOT isEmailTicket (prevents stale-items race on ticket switch).
  useEffect(() => {
    if (!isEmailTicket || chainInitDoneRef.current) return;
    const msgs = (items || []).filter(i => i.kind === 'msg');

    // Find the last message with a full chain (contains gmail_quote).
    let chainIdx = -1;
    for (let i = msgs.length - 1; i >= 0; i--) {
      const msg = msgs[i];
      const isAtt = msg.messageType === 'IMAGE' || msg.messageType === 'FILE';
      if (msg.htmlContent && !isAtt && msg.htmlContent.includes('gmail_quote')) {
        chainIdx = i;
        break;
      }
    }
    // Fall back to last message with any htmlContent if no chain found
    if (chainIdx === -1) {
      for (let i = msgs.length - 1; i >= 0; i--) {
        const isAtt = msgs[i].messageType === 'IMAGE' || msgs[i].messageType === 'FILE';
        if (msgs[i].htmlContent && !isAtt) {
          chainIdx = i;
          break;
        }
      }
    }
    if (chainIdx === -1) return;

    const getAttr = msg => {
      const isCust = msg.who === 'user' || msg.who === 'customer';
      const email = isCust
        ? activeTicket?.email_from || ''
        : activeTicket?.email_to || '';
      const name = isCust
        ? activeTicket?.customer_name || msg.name || email || 'Customer'
        : DEFAULT_FROM_NAME;
      const dateObj = msg.rawTs ? new Date(msg.rawTs) : new Date();
      return buildAttribution(dateObj, name, email);
    };

    // Start with the chain message's htmlContent
    let chain = wrapInGmailQuote(
      getAttr(msgs[chainIdx]),
      sanitizeHtml(msgs[chainIdx].htmlContent),
    );

    // Layer any newer messages on top
    for (let i = chainIdx + 1; i < msgs.length; i++) {
      const msg = msgs[i];
      const isAtt = msg.messageType === 'IMAGE' || msg.messageType === 'FILE';
      if (isAtt) continue;
      const body = msg.htmlContent
        ? sanitizeHtml(msg.htmlContent)
        : '<div dir="ltr">' + escapeHtml(msg.body || '') + '</div>';
      const content = body + '<br>' + chain;
      chain = wrapInGmailQuote(getAttr(msg), content);
    }

    chainRef.current = chain;
    chainInitDoneRef.current = true;
    syncQuotedArea(quotedAreaRef, chainRef.current);
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-populate quoted area when switching back from internal note tab
  useEffect(() => {
    if (!internal && quotedTextIncluded && chainRef.current) {
      setTimeout(() => {
        if (quotedAreaRef.current && !quotedAreaRef.current.innerHTML) {
          quotedAreaRef.current.innerHTML = sanitizeHtml(chainRef.current);
        }
      }, 0);
    }
  }, [internal, quotedTextIncluded]);

  // Toggle quoted text — controls UI visibility only.
  const toggleQuotedText = useCallback(() => {
    if (quotedTextIncluded) {
      setQuotedTextIncluded(false);
      return;
    }
    if (!chainRef.current) return;
    setQuotedTextIncluded(true);
    syncQuotedArea(quotedAreaRef, chainRef.current);
  }, [quotedTextIncluded]);

  // Update chain when a customer message arrives via PubNub
  const growChainFromPubnub = useCallback(
    newMsg => {
      const isAttachment =
        newMsg.messageType === 'IMAGE' || newMsg.messageType === 'FILE';
      if ((newMsg.who !== 'user' && newMsg.who !== 'customer') || isAttachment) return;

      const custEmail = newMsg.senderEmail || activeTicket?.email_from || '';
      const custName =
        activeTicket?.customer_name || newMsg.name || custEmail || 'Customer';
      const custDateObj = newMsg.rawTs ? new Date(newMsg.rawTs) : new Date();
      const attribution = buildAttribution(custDateObj, custName, custEmail);

      if (newMsg.htmlContent) {
        chainRef.current = wrapInGmailQuote(
          attribution,
          sanitizeHtml(newMsg.htmlContent),
        );
      } else if (newMsg.body) {
        const bodyHtml = '<div dir="ltr">' + escapeHtml(newMsg.body) + '</div>';
        const content = chainRef.current
          ? bodyHtml + '<br>' + chainRef.current
          : bodyHtml;
        chainRef.current = wrapInGmailQuote(attribution, content);
      }

      if (quotedAreaRef.current) {
        quotedAreaRef.current.innerHTML = sanitizeHtml(chainRef.current);
      }
    },
    [activeTicket?.email_from, activeTicket?.customer_name],
  );

  // Update chain after agent sends a reply
  const growChainFromAgentReply = useCallback(
    (content, dateObj) => {
      const agentEmail = activeTicket?.email_to || DEFAULT_FROM_EMAIL;
      const agentName = DEFAULT_FROM_NAME;
      const attribution = buildAttribution(dateObj, agentName, agentEmail);

      const bodyHtml = '<div dir="ltr">' + escapeHtml(content) + '</div>';
      const fullContent = chainRef.current
        ? bodyHtml + '<br>' + chainRef.current
        : bodyHtml;
      chainRef.current = wrapInGmailQuote(attribution, fullContent);

      if (quotedAreaRef.current) {
        quotedAreaRef.current.innerHTML = sanitizeHtml(chainRef.current);
      }
    },
    [activeTicket?.email_to],
  );

  // Build the full html_content for the send payload.
  // When Quote Reply is ON, includes the chain (from DOM — agent may have edited it).
  // When OFF, sends only the reply text without quoted chain.
  const buildSendHtml = useCallback(
    replyText => {
      const escaped = escapeHtml(replyText);
      const chain =
        quotedTextIncluded && quotedAreaRef.current
          ? sanitizeHtml(quotedAreaRef.current.innerHTML)
          : '';
      return buildReplyHtml(escaped, chain);
    },
    [quotedTextIncluded],
  );

  return {
    chainRef,
    quotedAreaRef,
    quotedTextIncluded,
    toggleQuotedText,
    growChainFromPubnub,
    growChainFromAgentReply,
    buildSendHtml,
  };
};

export default useEmailChain;
